import { LoaderCircle, Mic, Square, Wand2, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { api } from "@/api";

const SpeechRecognition = typeof window !== "undefined" ? window.SpeechRecognition || window.webkitSpeechRecognition : null;

export const VoiceInstructionPanel = ({ ticketId, onRegenerated, onClose, onSubmit, submitLabel = "Régénérer la réponse" }) => {
  const [text, setText] = useState("");
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const recognitionRef = useRef(null);
  const recorderRef = useRef(null);
  const baseTextRef = useRef("");

  useEffect(() => () => stopRecording(true), []); // eslint-disable-line react-hooks/exhaustive-deps

  const startBrowserRecognition = () => {
    const recognition = new SpeechRecognition();
    recognition.lang = "fr-FR";
    recognition.continuous = true;
    recognition.interimResults = true;
    baseTextRef.current = text ? `${text.trim()} ` : "";
    recognition.onresult = (event) => {
      let finalText = "";
      let interim = "";
      for (const result of event.results) {
        if (result.isFinal) finalText += `${result[0].transcript} `;
        else interim += result[0].transcript;
      }
      setText(`${baseTextRef.current}${finalText}${interim}`.replace(/\s+/g, " ").trimStart());
    };
    recognition.onerror = (event) => {
      if (event.error === "not-allowed") toast.error("Accès au micro refusé par le navigateur.");
      else if (event.error !== "aborted") startWhisperRecording();
      setRecording(false);
    };
    recognition.onend = () => setRecording(false);
    recognition.start();
    recognitionRef.current = recognition;
    setRecording(true);
  };

  const startWhisperRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks = [];
      recorder.ondataavailable = (event) => chunks.push(event.data);
      recorder.onstop = async () => {
        stream.getTracks().forEach((track) => track.stop());
        setRecording(false);
        const blob = new Blob(chunks, { type: recorder.mimeType || "audio/webm" });
        if (blob.size < 1000) return;
        setTranscribing(true);
        try {
          const form = new FormData();
          form.append("audio", blob, "consigne.webm");
          const { data } = await api.post("/ai/transcribe", form, { headers: { "Content-Type": "multipart/form-data" } });
          setText((current) => `${current ? `${current.trim()} ` : ""}${data.text}`.trim());
        } catch (error) {
          toast.error(error.response?.data?.detail || "La transcription a échoué.");
        } finally {
          setTranscribing(false);
        }
      };
      recorder.start();
      recorderRef.current = recorder;
      setRecording(true);
    } catch {
      toast.error("Impossible d’accéder au micro.");
    }
  };

  const stopRecording = (silent = false) => {
    if (recognitionRef.current) {
      recognitionRef.current.onend = null;
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      if (silent) recorderRef.current.onstop = null;
      recorderRef.current.stop();
      recorderRef.current = null;
    }
    if (!silent) setRecording(false);
  };

  const toggleMic = () => {
    if (recording) return stopRecording();
    if (SpeechRecognition) return startBrowserRecognition();
    return startWhisperRecording();
  };

  const regenerate = async () => {
    stopRecording();
    if (!text.trim()) return toast.error("Dictez ou tapez une consigne d’abord.");
    setGenerating(true);
    try {
      if (onSubmit) {
        await onSubmit(text.trim());
      } else {
        const { data } = await api.post(`/inbox/tickets/${ticketId}/regenerate`, { instruction: text.trim() });
        toast.success(data.message);
        onRegenerated();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "La régénération a échoué.");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="voice-panel" data-testid={`voice-panel-${ticketId}`}>
      <div className="voice-panel-head">
        <span className="message-label"><Wand2 size={12} /> Réorienter l’IA</span>
        <button type="button" className="voice-close" onClick={() => { stopRecording(true); onClose(); }} aria-label="Fermer" data-testid={`voice-close-${ticketId}`}><X size={14} /></button>
      </div>
      <div className="voice-row">
        <button
          type="button"
          className={`mic-button${recording ? " recording" : ""}`}
          onClick={toggleMic}
          disabled={transcribing || generating}
          title={recording ? "Arrêter la dictée" : "Dicter la consigne"}
          aria-label={recording ? "Arrêter la dictée" : "Dicter la consigne"}
          data-testid={`voice-mic-${ticketId}`}
        >
          {recording ? <Square size={18} /> : <Mic size={18} />}
        </button>
        <textarea
          rows={3}
          value={text}
          onChange={(event) => setText(event.target.value)}
          placeholder={recording ? "Je vous écoute…" : "Ex. : « Sois plus ferme, rappelle que le devis expire fin de mois »"}
          data-testid={`voice-text-${ticketId}`}
        />
      </div>
      <p className="voice-hint" data-testid={`voice-hint-${ticketId}`}>
        {recording ? "🔴 Dictée en cours — cliquez sur le carré pour arrêter." : transcribing ? "Transcription en cours…" : SpeechRecognition ? "Micro du navigateur (texte en direct). Vous pouvez corriger au clavier." : "Micro via Whisper : le texte apparaît à la fin de l’enregistrement."}
      </p>
      <div className="button-row">
        <button type="button" className="primary-button voice-generate" onClick={regenerate} disabled={generating || transcribing || !text.trim()} data-testid={`voice-regenerate-${ticketId}`}>
          {generating ? <LoaderCircle size={14} className="spinning" /> : <Wand2 size={14} />}{generating ? "Régénération…" : submitLabel}
        </button>
      </div>
    </div>
  );
};
